import classNames from 'classnames'
import { t } from 'i18next'
import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { API_ACCOUNT, API_ACCOUNTS } from '../../../../constants'
import { Account } from '../../../../model/account.model'
import { DropdownItem } from '../../../../model/dropdownItem.model'
import { RequestMethod, UserAccountType } from '../../../../model/enums/enums'
import { Person } from '../../../../model/person.model'
import {
  setAccounts,
  updateAccount,
} from '../../../../redux/basic/account/accountActions'

import {
  apprenticeAdded,
  apprenticeRemoved,
} from '../../../../redux/basic/apprentice/apprenticeReducer'
import {
  employeeAdded,
  employeeRemoved,
  employeeUpdated,
} from '../../../../redux/basic/employee/employeeReducer'
import {
  personAdded,
  personRemoved,
} from '../../../../redux/basic/person/personReducer'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { getUserAvatarSrc } from '../../../../util/getAvatar'
import { BackButton } from '../../../util/button/BackButton'
import { showErrorToast } from '../../../util/checkmark-toast/CheckmarkToast'

export const EditAccount = (): JSX.Element => {
  const accounts = useAppSelector((state) => state.accounts)
  const accountlessPersons = useAppSelector((state) => state.persons)
  const employees = useAppSelector((state) => state.employees)
  const apprentices = useAppSelector((state) => state.apprentices)
  const all: Person[] = useAppSelector((state) =>
    state.apprentices.concat(state.employees)
  )
  const [pers, setPers] = useState<Person>()

  // we need to deactivate the update button until we receive a response,
  // else we can get a timing issue with the identity platform
  const [updating, setUpdating] = useState<boolean>(false)

  const navigate = useNavigate()
  const location = useLocation()
  const toast = useRef<Toast>(null)

  const methods = useForm<Account>({
    reValidateMode: 'onBlur',
    mode: 'onSubmit',
    shouldFocusError: false,
  })

  useEffect(() => {
    const setupView = async () => {
      if (location) {
        // the location is at this point ...Company/<companyId> and we want to extract the Company id.
        // Reason: If someone sends this link we want to display the data of this url
        const n = location.pathname.lastIndexOf('/')
        const accountId = location.pathname.substring(n + 1)

        if (accounts.length === 0) {
          const result = await handleRequest<Account[]>(
            API_ACCOUNTS(),
            RequestMethod.GET
          )
          if (result.data && !result.error) {
            dispatcher(setAccounts(result.data))
          }
        }

        const id = parseInt(accountId)
        if (!id || id === 0) {
          console.warn(
            '[Edit Account] AccountId not defined, redirecting to Account overview.'
          )
          navigate(AuthRoutes.accounts)
          return
        }

        const acc = accounts.find((account) => account.account_id === id)

        methods.reset(acc)
        if (acc) {
          const user = all.find((p) => p.person_id === acc.id_person)
          setPers({ ...user })
        }
      }
    }
    setupView()
  }, [location, accounts])

  const isActiveOptions: DropdownItem[] = [
    { label: t<string>('core.active'), value: 1 },
    { label: t<string>('core.inactive'), value: 0 },
  ]

  const userRoles: DropdownItem[] = [
    { label: t<string>('core.role.apprentice'), value: 1 },
    { label: t<string>('core.role.ausbilder'), value: 2 },
    { label: t<string>('core.role.berufsbildner'), value: 3 },
    { label: t<string>('core.role.admin'), value: 4 },
  ]

  const copyPerson = (person: Person | undefined, newRole: UserAccountType) => {
    // create deep nested clone (!expensive operation)
    if (!person) {
      //TODO ask for hard-refreshing the page
      console.warn('Something went not as expected moving the person')
      return undefined
    }
    const copiedPerson = JSON.parse(JSON.stringify(person))
    copiedPerson.user_account_type = newRole
    return copiedPerson
  }
  const addPersonToStore = (person: Person, role: UserAccountType) => {
    switch (role) {
      case UserAccountType.Apprentice:
        dispatcher(apprenticeAdded(person))
        break
      case UserAccountType.NoAccount:
        dispatcher(personAdded(person))
        break
      case UserAccountType.Praxisbildner:
      case UserAccountType.Berufsbildner:
      case UserAccountType.Admin:
        dispatcher(employeeAdded(person))
        break
      default:
        console.warn('Case not implemented for role', role)
    }
  }

  const isTypeEmployee = (role: UserAccountType) => {
    return (
      role === UserAccountType.Admin ||
      role === UserAccountType.Berufsbildner ||
      role === UserAccountType.Praxisbildner
    )
  }

  const staysEmployee = (
    newRole: UserAccountType,
    oldRole: UserAccountType
  ) => {
    return isTypeEmployee(oldRole) && isTypeEmployee(newRole)
  }
  /**
   * when the person role changes
   * @param personId
   * @param oldRole
   * @param newRole
   */
  const updatePersonRole = (
    personId: number,
    oldRole: UserAccountType | undefined,
    newRole: UserAccountType
  ) => {
    let personObject: Person | undefined

    switch (oldRole) {
      case UserAccountType.NoAccount:
        personObject = copyPerson(
          accountlessPersons.find((p) => p.person_id === personId),
          newRole
        )
        if (personObject) {
          dispatcher(personRemoved(personId))
          addPersonToStore(personObject, newRole)
        }
        break
      case UserAccountType.Apprentice:
        personObject = copyPerson(
          apprentices.find((p) => p.person_id === personId),
          newRole
        )
        if (personObject) {
          dispatcher(apprenticeRemoved(personId))
          addPersonToStore(personObject, newRole)
        }
        break
      case UserAccountType.Praxisbildner:
      case UserAccountType.Berufsbildner:
      case UserAccountType.Admin:
        personObject = copyPerson(
          employees.find((p) => p.person_id === personId),
          newRole
        )
        if (personObject) {
          if (staysEmployee(newRole, oldRole)) {
            dispatcher(employeeUpdated(personObject))
          } else {
            dispatcher(employeeRemoved(personId))
            addPersonToStore(personObject, newRole)
          }
        }
        break
      default:
        console.warn('[Edit Account] Case not handled.')
    }
  }

  const dispatcher = useAppDispatch()
  const onSubmit = async (account: Account) => {
    if (!account) {
      console.warn('[Edit Account] Account not set')
      return
    }
    setUpdating(true)
    const oldUserRole = accounts.find(
      (acc) => acc.account_id === account.account_id
    )?.user_account.id_user_account

    const result = await handleRequest<Account>(
      API_ACCOUNT(account.account_id),
      RequestMethod.PUT,
      account
    )
    setUpdating(false)
    if (result.data && !result.error) {
      console.info(
        `[Edit Account] Successfully edited the account of ${account.account_name}.`
      )
      dispatcher(updateAccount(result.data))

      const newUserRole = result.data.user_account.id_user_account

      if (oldUserRole !== newUserRole) {
        updatePersonRole(account.id_person, oldUserRole, newUserRole)
      }

      methods.reset(result.data)
      toast?.current?.show({
        className: 'mt-7',
        closable: true,
        summary: t<string>('core.action.edit.successful.title'),
        detail: t<string>('core.action.edit.successful.message'),
        severity: 'success',
        life: 8000,
      })
    } else {
      console.warn(
        `[Edit Account] Error occurred on editing the account of ${account.account_name}.`
      )

      if (result.httpStatus === 409) {
        showErrorToast(
          toast,
          t<string>('core.error.duplicated_email.title'),
          t<string>('core.error.duplicated_email.message')
        )
        return
      }
      toast?.current?.show({
        className: 'mt-7',
        closable: true,
        summary: t<string>('core.error.account.edit.title'),
        detail: t<string>('core.error.account.edit.message'),
        severity: 'error',
        life: 12000,
      })
    }
  }

  return (
    <div className={'mr-2'}>
      <BackButton
        translationKey={'navigation.go_account_management'}
        route={AuthRoutes.accounts}
      />
      <Avatar shape={'circle'} size={'xlarge'} image={getUserAvatarSrc(pers)} />
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <h3 className={'ml-0 pl-0'}>
          {t<string>('account.account')}:{' '}
          <b className={'ml-2'}>{methods.getValues('account_name')}</b>
        </h3>
        {pers && (
          <span className={'secondary-text small-text'}>
            {t<string>('account.contact_email')}:{' '}
            <span className={'ml-2 mb-2 '}>{pers?.email}</span>
          </span>
        )}
        <div className={'card pl-2 pt-4'}>
          <div className="formgrid grid">
            <div className={'field col-12 md:col-6'}>
              <label>
                {t<string>('account.email')}
                <span
                  className={
                    'ml-2 fa-solid fa-question-circle account-email-info icon-gray'
                  }
                />
              </label>
              <Controller
                name={'email'}
                control={methods.control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <InputText
                    name={field.name}
                    value={field.value ? field.value : ''}
                    onChange={field.onChange}
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      'w-full'
                    )}
                    type={'email'}
                  />
                )}
              />
            </div>
            <div className={'field col-12 md:col-3'}>
              <label>
                {t<string>('account.role')}
                <span
                  className={
                    'ml-2 fa-solid fa-exclamation-triangle account-role-info icon-orange'
                  }
                />
              </label>
              <Controller
                name={'user_account.id_user_account'}
                rules={{ required: true }}
                control={methods.control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    name={field.name}
                    value={field.value || -1}
                    options={userRoles}
                    onChange={field.onChange}
                    appendTo={document.body}
                    placeholder={t<string>('account.select_role')}
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      'w-full'
                    )}
                  />
                )}
              />
            </div>
            <div className={'field col-12 md:col-3'}>
              <label>
                {t<string>('account.status')}
                <span
                  className={
                    'ml-2 fa-solid fa-question-circle account-status-info icon-gray'
                  }
                />
              </label>
              <Controller
                name={'is_active'}
                control={methods.control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    name={field.name}
                    options={isActiveOptions}
                    value={field.value}
                    onChange={field.onChange}
                    className={classNames(
                      { 'p-invalid': fieldState.error },
                      'w-full'
                    )}
                  />
                )}
              />
            </div>
          </div>
          <div className={'flex mt-2 justify-content-end'}>
            <Button
              label={t<string>('core.cancel')}
              className={'p-button-text'}
              type={'button'}
              onClick={() => navigate(AuthRoutes.accounts)}
            />
            <Button
              className={'p-button-warning mr-0'}
              label={t<string>('account.edit', {
                name: methods.getValues('account_name'),
              })}
              type={'submit'}
              loading={updating}
              disabled={updating}
            />
          </div>
        </div>
        <Tooltip
          target={'.account-email-info'}
          content={t<string>('account.email_info')}
        />
        <Tooltip
          target={'.account-status-info'}
          content={t<string>('account.status_info')}
        />
        <Tooltip
          target={'.account-role-info'}
          content={t<string>('account.role_info')}
        />
      </form>
      <Toast ref={toast} position={'bottom-right'} />
    </div>
  )
}